import {throttle} from 'lodash'
import React, {useCallback, useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useDispatch, useSelector} from 'react-redux'
import {useLocation} from 'react-router-dom'

import {setGlobalConfigKey} from '~/reducers/global'
import {RootState} from '~/stores'
import {
  IMG_HOME_MOBILE_BG,
  IMG_HOME_WEB_BG,
  MOBILE_BREAKPOINT,
  RESIZE_THROTTLE_DELAY,
  SHOW_HOME_BG_MAP,
} from '~/utils/constant'
import {isBrowser} from '~/utils/helper'

import './index.scss'

type NavigatorWithUserAgentData = Navigator & {
  userAgentData?: {
    mobile?: boolean
  }
}

const isMobileDevice = () => {
  const navigatorWithUserAgentData = navigator as NavigatorWithUserAgentData
  const {userAgent} = navigator
  const isMobileUserAgent =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|OpenHarmony|HarmonyOS|HuaweiBrowser|ArkWeb/i.test(
      userAgent
    )
  const isIPadOS = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1
  const viewportCandidates = [
    window.visualViewport?.width,
    window.innerWidth,
    document.documentElement.clientWidth,
  ].filter((width): width is number => typeof width === 'number' && width > 0)
  const viewportWidth =
    viewportCandidates.length > 0
      ? Math.min(...viewportCandidates)
      : Number.POSITIVE_INFINITY

  return (
    navigatorWithUserAgentData.userAgentData?.mobile === true ||
    isMobileUserAgent ||
    isIPadOS ||
    viewportWidth < MOBILE_BREAKPOINT
  )
}

export default function App(props) {
  const {children} = props
  const {t, i18n} = useTranslation()
  const location = useLocation()
  const dispatch = useDispatch()
  const {globalConfig} = useSelector((state: RootState) => state.global)
  const [isLoadFinished, setIsLoadFinished] = useState(false)

  const BG_IMG =
    globalConfig.OSType === 'web' ? IMG_HOME_WEB_BG : IMG_HOME_MOBILE_BG

  const getIsShowHomeBg = () => {
    // 在此处可以扩展使用redux动态控制
    return SHOW_HOME_BG_MAP[location.pathname]
  }

  const syncDeviceMode = useCallback(() => {
    dispatch(
      setGlobalConfigKey({
        key: 'OSType',
        value: isMobileDevice() ? 'mobile' : 'web',
      })
    )
  }, [dispatch])

  const throttledSyncDeviceMode = useCallback(
    throttle(syncDeviceMode, RESIZE_THROTTLE_DELAY, {trailing: true}),
    [syncDeviceMode]
  )

  useEffect(() => {
    syncDeviceMode()
    window.addEventListener('resize', throttledSyncDeviceMode)
    window.addEventListener('orientationchange', throttledSyncDeviceMode)
    window.visualViewport?.addEventListener('resize', throttledSyncDeviceMode)

    return () => {
      window.removeEventListener('resize', throttledSyncDeviceMode)
      window.removeEventListener('orientationchange', throttledSyncDeviceMode)
      window.visualViewport?.removeEventListener(
        'resize',
        throttledSyncDeviceMode
      )
      throttledSyncDeviceMode.cancel()
    }
  }, [syncDeviceMode, throttledSyncDeviceMode])

  useEffect(() => {
    document.title = t('dokimo-予你一场心动')
  }, [t, i18n.language])

  useEffect(() => {
    if (isBrowser()) {
      if (document.readyState === 'complete') {
        setIsLoadFinished(true)
      } else if (document.readyState === 'interactive') {
        window.addEventListener('load', () => {
          setIsLoadFinished(true)
        })
      } else {
        window.addEventListener('load', () => {
          setIsLoadFinished(true)
        })
      }
    }
  }, [])

  if (!isLoadFinished) {
    return null
  }

  return (
    <div
      className={`contentBox ${
        globalConfig.OSType === 'mobile' ? 'mobile' : ''
      }`}
      style={{
        backgroundImage: `url(${getIsShowHomeBg() ? BG_IMG : ''})`,
      }}
    >
      <div className="contentWrapper">{children}</div>
    </div>
  )
}
